import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ReservationApiStatus = 'CONFIRMED' | 'SEATED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';

export interface Reservation {
  id: string;
  location_id: string;
  table_id: string;
  table_number: string;
  customer_name: string;
  customer_phone: string;
  party_size: number;
  reservation_date: string;
  reservation_time: string;
  duration_minutes: number;
  status: ReservationApiStatus;
  notes?: string;
  created_at: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ReservationListResponse {
  data: Reservation[];
  meta: PaginationMeta;
}

interface ReservationListFilters {
  location_id?: string;
  date?: string;
  status?: string;
  date_after?: string;
  date_before?: string;
  page?: number;
  limit?: number;
}

export interface ReservationStats {
  total: number;
  confirmed: number;
  no_show: number;
  no_show_rate: number;
  peak_hour: number | null;
}

interface CreateReservationRequest {
  location_id: string;
  table_id: string;
  customer_name: string;
  customer_phone: string;
  party_size: number;
  reservation_date: string;
  reservation_time: string;
  duration_minutes?: number;
  notes?: string;
}

interface UpdateReservationStatusRequest {
  status: 'SEATED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const reservationService = {
  async listReservations(filters: ReservationListFilters = {}): Promise<ReservationListResponse> {
    const query = buildQuery(filters as Record<string, string | number | undefined>);
    return apiClientPaginated<Reservation[]>(`/reservations${query}`);
  },

  async getStats(
    filters: Omit<ReservationListFilters, 'page' | 'limit'> = {},
  ): Promise<ReservationStats> {
    const query = buildQuery(filters as Record<string, string | number | undefined>);
    return apiClient<ReservationStats>(`/reservations/stats${query}`);
  },

  async createReservation(dto: CreateReservationRequest): Promise<Reservation> {
    return apiClient<Reservation>('/reservations', {
      method: 'POST',
      body: dto,
    });
  },

  async updateStatus(id: string, dto: UpdateReservationStatusRequest): Promise<Reservation> {
    return apiClient<Reservation>(`/reservations/${id}/status`, {
      method: 'PATCH',
      body: dto,
    });
  },
};
